// @ts-nocheck
import { Cartridge } from "./cartridge.js";
import { CartridgeType } from "./cartridge-type.enum.js";
export class Mbc3Cartridge extends Cartridge {
    romBank = 0b0;
    isRamAndRtcEnabled = false;
    ramBankOrRtcSelection = 0;
    ramData;
    ramDataView;
    ramBytes;
    // Used to debounce calls to on sram write callback. Allows auto saving data without
    // hammering writes
    writeTimeout;
    onSramWrite;
    //TODO: Implement debounced callback for saving
    constructor(gameDataView) {
        super(gameDataView);
        this.ramData = new ArrayBuffer(this.ramSize);
        this.ramDataView = new DataView(this.ramData);
        this.ramBytes = new Uint8Array(this.ramDataView.buffer);
        this.ramBytes.fill(0xff);
    }
    setRam(sramArrayBuffer) {
        this.ramData = sramArrayBuffer;
        this.ramDataView = new DataView(this.ramData);
        this.ramBytes = new Uint8Array(this.ramDataView.buffer);
    }
    dumpRam() {
        return this.ramDataView.buffer;
    }
    writeByte(address, value) {
        const sramWrite = (address, value) => {
            this.ramDataView.setUint8(address, value);
            if (this.type === CartridgeType.MBC3_RAM_BATTERY && this.onSramWrite) {
                clearTimeout(this.writeTimeout);
                this.writeTimeout = setTimeout(() => this.onSramWrite(this.ramData), 500);
            }
        };
        this.write(address, value, sramWrite);
    }
    writeWord(address, value) {
        const sramWrite = (address, value) => {
            this.ramDataView.setUint16(address, value, true);
            if (this.type === CartridgeType.MBC3_RAM_BATTERY && this.onSramWrite) {
                clearTimeout(this.writeTimeout);
                this.writeTimeout = setTimeout(() => this.onSramWrite(this.ramData), 500);
            }
        };
        this.write(address, value, sramWrite);
    }
    readByte(address) {
        const cartridgeRead = (address) => this.gameDataView.getUint8(address);
        const sramRead = (address) => this.ramDataView.getUint8(address);
        return this.read(address, cartridgeRead, sramRead);
    }
    readSignedByte(address) {
        const cartridgeRead = (address) => this.gameDataView.getInt8(address);
        const sramRead = (address) => this.ramDataView.getInt8(address);
        return this.read(address, cartridgeRead, sramRead);
    }
    readWord(address) {
        const cartridgeRead = (address) => this.gameDataView.getUint16(address, true);
        const sramRead = (address) => this.ramDataView.getUint16(address, true);
        return this.read(address, cartridgeRead, sramRead);
    }
    // Flat ROM offset for an address in the ROM window, mirroring the bank
    // math in read() exactly. Without this override the base returns the
    // raw CPU address, ignoring romBank — so the host's exec-hook /
    // write_rom surface (translatePC / fireExecHooks / writeRomByte) would
    // miss every hook armed on banked MBC3 code in $4000-$7fff, and collide
    // banks at the same $4xxx address. (MultiNostalgia patch — see
    // ../../PATCHES.md; mirrors the Mbc1Cartridge override.)
    romOffsetForAddress(address) {
        const maskedAddress = address & 0x3fff;
        if (address >= 0x0000 && address <= 0x3fff) {
            // Fixed bank 0: the address is already its own flat offset.
            return address;
        }
        // $4000-$7fff: the switchable bank.
        return ((this.romBank << 14) + maskedAddress) & (this.romSize - 1);
    }
    read(address, readFromCartridge, readFromSram) {
        const maskedAddress = address & 0x3fff;
        if (address >= 0x0000 && address <= 0x3fff) {
            return readFromCartridge(address);
        }
        else if (address >= 0x4000 && address <= 0x7fff) {
            const bankNumber = this.romBank;
            const bankCorrectedAddress = (bankNumber << 14) + maskedAddress;
            const wrappedForSize = bankCorrectedAddress & (this.romSize - 1);
            return readFromCartridge(wrappedForSize);
        }
        else {
            // RAM or RTC
            if (!this.isRamAndRtcEnabled) {
                return 0xff;
            }
            if (this.ramBankOrRtcSelection <= 3) {
                const maskedAddress = address & 0x1fff;
                const bankedAddress = (this.ramBankOrRtcSelection << 13) + maskedAddress;
                return readFromSram(bankedAddress);
            }
            // RTC register selected ($08-$0C). The carts we run (Pokémon R/B) are
            // MBC3 with the clock unpopulated, so there is nothing to read back —
            // but a memory read MUST still yield a concrete byte. The old
            // fall-through returned `undefined`, which reaches the CPU as NaN and
            // silently derails execution. Return open-bus $ff (an absent RTC reads
            // back as all-ones on hardware). (MultiNostalgia patch — see PATCHES.md.)
            return 0xff;
        }
    }
    write(address, value, writeToSram) {
        if (this.isRamAndTimerGate(address)) {
            const valueToEnableRam = 0b1010;
            const lowerNibble = value & 0b1111;
            this.isRamAndRtcEnabled = lowerNibble === valueToEnableRam;
        }
        else if (this.isRomBank(address)) {
            const masked = value & 0b1111111;
            this.romBank = masked === 0 ? 1 : masked; // zero not allowed in bank 1
        }
        else if (this.isRamBankNumberOrRtcRegisterSelect(address)) {
            this.ramBankOrRtcSelection = value;
        }
        else if (this.isLatchClockData(address)) {
            // RTC latch clock ($6000-$7FFF). Latching copies the live clock into
            // the readable RTC registers on an MBC3-with-RTC cart. Our carts
            // (Pokémon R/B) are MBC3 with NO clock populated, yet the game writes
            // here anyway — its save/PC-box code reuses the MBC1 rBMODE ($6000)
            // address (see save.asm `ld [rBMODE], a`). With no RTC there is nothing
            // to latch, so this is a legitimate no-op. Previously it logged
            // 'not implemented' on every such write, flooding the console during
            // any save. (MultiNostalgia patch — see PATCHES.md.)
        }
        else if (this.isRamBankOrRtcRegister(address)) {
            const maskedAddress = address & 0x1fff;
            if (this.ramBankOrRtcSelection <= 3) {
                const bankedAddress = (this.ramBankOrRtcSelection << 13) + maskedAddress;
                writeToSram(bankedAddress, value);
            }
        }
    }
    isRamAndTimerGate(address) {
        return address >= 0x0000 && address <= 0x1fff;
    }
    isRomBank(address) {
        return address >= 0x2000 && address <= 0x3fff;
    }
    isRamBankNumberOrRtcRegisterSelect(address) {
        return address >= 0x4000 && address <= 0x5fff;
    }
    isLatchClockData(address) {
        return address >= 0x6000 && address <= 0x7fff;
    }
    isRamBankOrRtcRegister(address) {
        return address >= 0xa000 && address <= 0xbfff;
    }
}
//# sourceMappingURL=mbc3-cartridge.js.map